// The frequency itself is a user setting ("updates.check-frequency" in the
// AppSettings schema); this module only owns the interval gating and the
// last-check timestamp. The timestamp is runtime state, not a user setting —
// it stays in localStorage and never goes to config.yaml.
export enum UpdateCheckFrequency {
	EveryLaunch = "every-launch",
	TwiceDaily = "twice-daily",
	Daily = "daily",
	Weekly = "weekly",
	Never = "never",
}

const LAST_CHECK_KEY = "last-update-check";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;

// Windows opened within one launch share a single launch check.
const LAUNCH_CHECK_DEDUP = 15 * MINUTE;
const WEEK = 7 * 24 * HOUR;

const CHECK_PERIODS: Partial<Record<UpdateCheckFrequency, number>> = {
	[UpdateCheckFrequency.TwiceDaily]: 12 * HOUR,
	[UpdateCheckFrequency.Daily]: 24 * HOUR,
};

// A stamp from the future was taken by a clock that ran fast; waiting for it would block checks until then.
const sinceLastCheck = (): number => {
	const elapsed = Date.now() - Number(window.localStorage.getItem(LAST_CHECK_KEY) ?? 0);
	return elapsed < 0 ? Number.POSITIVE_INFINITY : elapsed;
};

// A timer frequency gets its launch check from the Rust timer, once per app process rather than per window.
export const shouldCheckOnLaunch = (frequency: UpdateCheckFrequency): boolean => {
	if (frequency === UpdateCheckFrequency.Never || CHECK_PERIODS[frequency] !== undefined) return false;
	const gap = frequency === UpdateCheckFrequency.Weekly ? WEEK : LAUNCH_CHECK_DEDUP;
	return sinceLastCheck() > gap;
};

export type UpdateCheckSchedule = { enabled: boolean; periodMs: number | null };

export const getCheckSchedule = (frequency: UpdateCheckFrequency): UpdateCheckSchedule => ({
	enabled: frequency !== UpdateCheckFrequency.Never,
	periodMs: CHECK_PERIODS[frequency] ?? null,
});

export const markUpdateCheck = () => {
	window.localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
};
